import { CommonModule, DatePipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AdminApiService } from '../../../core/api-services/admin-api.service';
import { LookupsApiService } from '../../../core/api-services/lookups-api.services';
import { AuthorizationService } from '../../../core/authorization/authorization.service';
import { Permissions } from '../../../core/authorization/permissions';
import { PaginationComponent } from '../../../shared/pagination/pagination';
import {
  AdminCreateUserRequest,
  AdminUser,
  AdminUserStatus,
  UpdateUserContactRequest,
} from '../../../core/models/admin-user';
import { Role } from '../../../core/models/role';
import { IdentityType } from '../../../core/models/identity-type';
import { Institution } from '../../../core/models/institution';
import { Course } from '../../../core/models/course';
import { AuditLog } from '../../../core/models/audit-log';
import { isValidSaIdNumber } from '../../../core/validators/south-african-id.validator';
import { ChangeSummaryItem } from '../../../core/interfaces/change-summary-item';

@Component({
  selector: 'app-admin-users',
  imports: [CommonModule, DatePipe, FormsModule, PaginationComponent],
  templateUrl: './users.html',
  styleUrl: './users.scss',
})
export class AdminUsersComponent implements OnInit {
  private readonly adminApi = inject(AdminApiService);
  private readonly lookups = inject(LookupsApiService);
  readonly authorization = inject(AuthorizationService);
  readonly permissions = Permissions;

  users: AdminUser[] = [];
  roles: Role[] = [];
  identityTypes: IdentityType[] = [];
  institutions: Institution[] = [];
  courses: Course[] = [];

  loading = false;
  saving = false;
  errorMessage = '';
  successMessage = '';

  searchQuery = '';
  statusFilter = '';
  currentPage = 1;
  readonly pageSize = 8;
  readonly statusOptions: AdminUserStatus[] = ['ACTIVE', 'BLOCKED', 'INACTIVE', 'DELETED'];

  showCreateModal = false;
  showContactModal = false;
  showRolesModal = false;
  showActivityModal = false;

  selectedUser: AdminUser | null = null;
  selectedRoleIds: number[] = [];
  activityLogs: AuditLog[] = [];
  activityLoading = false;
  activityError = '';

  contactForm: UpdateUserContactRequest = {
    emailAddress: '',
    phoneNumber: '',
    address: '',
  };

  createForm: AdminCreateUserRequest = {
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    address: '',
    identityTypeId: '',
    identityNumber: '',
    userName: '',
    password: '',
    roleIds: [],
    institutionId: null,
    courseId: null,
  };

  ngOnInit(): void {
    this.loadUsers();
    this.loadRoles();
    this.loadIdentityTypes();
    this.loadInstitutions();
  }

  get canManageRoles(): boolean {
    return this.authorization.hasPermission(this.permissions.RoleAssign);
  }

  get canCreateUser(): boolean {
    return this.authorization.hasPermission(this.permissions.UserManage) && this.canManageRoles;
  }

  get filteredUsers(): AdminUser[] {
    let results = [...this.users];

    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      results = results.filter(user =>
        this.userName(user).toLowerCase().includes(query) ||
        String(user.userName ?? '').toLowerCase().includes(query) ||
        String(user.emailAddress ?? '').toLowerCase().includes(query) ||
        String(user.identityNumber ?? '').toLowerCase().includes(query)
      );
    }

    if (this.statusFilter) {
      results = results.filter(user => user.status === this.statusFilter);
    }

    return results;
  }

  get paginatedUsers(): AdminUser[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredUsers.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredUsers.length / this.pageSize));
  }

  get showingFrom(): number {
    if (this.filteredUsers.length === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingTo(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredUsers.length);
  }

  loadUsers(): void {
    this.loading = true;
    this.errorMessage = '';
    this.adminApi.getUsers().subscribe({
      next: users => {
        this.users = users;
        this.loading = false;
      },
      error: error => {
        this.errorMessage = this.extractErrorMessage(error);
        this.loading = false;
      },
    });
  }

  loadRoles(): void {
    this.lookups.getRoles().subscribe({
      next: roles => {
        this.roles = roles;
        // If the roles modal was opened before the lookup resolved, sync the
        // checkbox selection once the role list arrives.
        if (this.showRolesModal && this.selectedUser) {
          this.selectedRoleIds = this.resolveRoleIds(this.selectedUser);
        }
      },
      error: () => {
        // Role options are non-critical; the user list still works.
        this.roles = [];
      },
    });
  }

  loadIdentityTypes(): void {
    this.lookups.getIdentityTypes().subscribe({
      next: identityTypes => {
        this.identityTypes = identityTypes;
      },
      error: () => {
        this.identityTypes = [];
      },
    });
  }

  loadInstitutions(): void {
    this.lookups.getInstitutions().subscribe({
      next: institutions => {
        this.institutions = institutions;
      },
      error: () => {
        this.institutions = [];
      },
    });
  }

  onFiltersChanged(): void {
    this.currentPage = 1;
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  userName(user: AdminUser): string {
    return [user.firstName, user.lastName].filter(Boolean).join(' ');
  }

  openCreateModal(): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.createForm = {
      firstName: '',
      lastName: '',
      emailAddress: '',
      phoneNumber: '',
      address: '',
      identityTypeId: '',
      identityNumber: '',
      userName: '',
      password: '',
      roleIds: [],
      institutionId: null,
      courseId: null,
    };
    this.courses = [];
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
  }

  createUser(): void {
    if (!this.createForm.identityTypeId) {
      this.errorMessage = 'Identity type is required.';
      return;
    }

    const identityTypeId = Number(this.createForm.identityTypeId);
    const selectedIdentityType = this.identityTypes.find(type => type.id === identityTypeId);
    const isPassport = selectedIdentityType?.name.trim().toLowerCase() === 'passport';
    if (!isPassport && !isValidSaIdNumber(String(this.createForm.identityNumber ?? '').trim())) {
      this.errorMessage = 'Enter a valid South African ID number (13 digits with a valid checksum).';
      return;
    }

    if (this.createForm.roleIds.length === 0) {
      this.errorMessage = 'Select at least one role.';
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.adminApi.createUser(this.createForm).subscribe({
      next: () => {
        this.saving = false;
        this.successMessage = `User "${this.createForm.userName}" created successfully.`;
        this.closeCreateModal();
        this.loadUsers();
      },
      error: error => {
        this.saving = false;
        this.errorMessage = this.extractErrorMessage(error);
      },
    });
  }

  onInstitutionChange(): void {
    this.createForm.courseId = null;
    this.courses = [];
    if (!this.createForm.institutionId) return;

    this.lookups.getInstitutionCourses(this.createForm.institutionId).subscribe({
      next: courses => {
        this.courses = courses;
      },
      error: () => {
        this.courses = [];
      },
    });
  }

  toggleCreateRole(roleId: number, checked: boolean): void {
    if (checked) {
      if (!this.createForm.roleIds.includes(roleId)) {
        this.createForm.roleIds.push(roleId);
      }
    } else {
      this.createForm.roleIds = this.createForm.roleIds.filter(id => id !== roleId);
    }
  }

  isCreateRoleSelected(roleId: number): boolean {
    return this.createForm.roleIds.includes(roleId);
  }

  openContactModal(user: AdminUser): void {
    this.selectedUser = user;
    this.errorMessage = '';
    this.successMessage = '';
    this.contactForm = {
      emailAddress: user.emailAddress ?? '',
      phoneNumber: user.phoneNumber ?? '',
      address: user.address ?? '',
    };
    this.showContactModal = true;
  }

  closeContactModal(): void {
    this.showContactModal = false;
    this.selectedUser = null;
  }

  saveContact(): void {
    if (!this.selectedUser) return;

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.adminApi.updateUserContact(this.selectedUser.id, this.contactForm).subscribe({
      next: message => {
        this.saving = false;
        this.successMessage = message;
        this.closeContactModal();
        this.loadUsers();
      },
      error: error => {
        this.saving = false;
        this.errorMessage = this.extractErrorMessage(error);
      },
    });
  }

  openRolesModal(user: AdminUser): void {
    this.selectedUser = user;
    this.errorMessage = '';
    this.successMessage = '';
    this.selectedRoleIds = this.resolveRoleIds(user);
    this.showRolesModal = true;
  }

  closeRolesModal(): void {
    this.showRolesModal = false;
    this.selectedUser = null;
    this.selectedRoleIds = [];
  }

  toggleRole(roleId: number, checked: boolean): void {
    if (checked) {
      if (!this.selectedRoleIds.includes(roleId)) {
        this.selectedRoleIds.push(roleId);
      }
    } else {
      this.selectedRoleIds = this.selectedRoleIds.filter(id => id !== roleId);
    }
  }

  isRoleSelected(roleId: number): boolean {
    return this.selectedRoleIds.includes(roleId);
  }

  saveRoles(): void {
    if (!this.selectedUser) return;

    if (this.selectedRoleIds.length === 0) {
      this.errorMessage = 'Select at least one role.';
      return;
    }

    this.saving = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.adminApi.updateUserRoles(this.selectedUser.id, { roleIds: this.selectedRoleIds }).subscribe({
      next: message => {
        this.saving = false;
        this.successMessage = message;
        this.closeRolesModal();
        this.loadUsers();
      },
      error: error => {
        this.saving = false;
        this.errorMessage = this.extractErrorMessage(error);
      },
    });
  }

  updateStatus(user: AdminUser, status: AdminUserStatus): void {
    this.errorMessage = '';
    this.successMessage = '';
    this.saving = true;
    this.adminApi.updateUserStatus(user.id, { status }).subscribe({
      next: message => {
        this.saving = false;
        this.successMessage = message;
        user.status = status;
        this.currentPage = Math.min(this.currentPage, this.totalPages);
      },
      error: error => {
        this.saving = false;
        this.errorMessage = this.extractErrorMessage(error);
        this.loadUsers();
      },
    });
  }

  openActivityModal(user: AdminUser): void {
    this.selectedUser = user;
    this.activityLogs = [];
    this.activityLoading = true;
    this.activityError = '';
    this.errorMessage = '';
    this.showActivityModal = true;

    this.adminApi.getUserActivityLogs(user.id).subscribe({
      next: logs => {
        this.activityLogs = logs;
        this.activityLoading = false;
      },
      error: error => {
        this.activityLoading = false;
        this.activityError = this.extractErrorMessage(error);
      },
    });
  }

  closeActivityModal(): void {
    this.showActivityModal = false;
    this.selectedUser = null;
    this.activityLogs = [];
    this.activityError = '';
  }

  getStatusClass(status: AdminUserStatus): string {
    switch (status) {
      case 'ACTIVE':
        return 'badge-success';
      case 'BLOCKED':
        return 'badge-danger';
      case 'INACTIVE':
        return 'badge-warning';
      case 'DELETED':
        return 'badge-neutral';
      default:
        return 'badge-neutral';
    }
  }

  getEntityLabel(log: AuditLog): string {
    if (log.entityName === 'UserRole') {
      return `Role assignment #${log.entityId}`;
    }
    return `${log.entityName} #${log.entityId}`;
  }

  getChangeSummary(changes: Record<string, unknown> | string | null | undefined): ChangeSummaryItem[] {
    const parsedChanges = this.parseChanges(changes);

    return Object.entries(parsedChanges)
      .map(([field, change]) => {
        const values = change as Record<string, unknown>;
        return {
          field: this.formatFieldName(field),
          oldValue: this.formatChangeValue(values['Old'] ?? values['old']),
          newValue: this.formatChangeValue(values['New'] ?? values['new']),
        };
      })
      .filter(change => change.oldValue !== change.newValue);
  }

  private resolveRoleIds(user: AdminUser): number[] {
    const userRoleNames = new Set((user.roles ?? []).map(name => name.toLowerCase()));
    return this.roles
      .filter(role => userRoleNames.has(role.name.toLowerCase()))
      .map(role => role.id);
  }

  private parseChanges(changes: Record<string, unknown> | string | null | undefined): Record<string, unknown> {
    if (!changes) return {};

    if (typeof changes !== 'string') return changes;

    try {
      const parsed = JSON.parse(changes);
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : {};
    } catch {
      return {};
    }
  }

  private formatFieldName(field: string): string {
    return field.replace(/Id\b/g, 'ID');
  }

  private formatChangeValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'string') return value;
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private extractErrorMessage(error: unknown): string {
    const err = error as { error?: { message?: string } };
    return err?.error?.message ?? 'The request could not be completed. Please try again.';
  }
}
